export const ACTIVATION_STAGES = [
	{ key: "signed_up", label: "Signed up" },
	{ key: "set_up", label: "Set up" },
	{ key: "help_center_imported", label: "Help center imported" },
	{ key: "help_center_live", label: "Help center live" },
	{ key: "domain_connected", label: "Domain connected" },
	{ key: "email_connected", label: "Email connected" },
	{ key: "channel_connected", label: "Channel connected" },
	{ key: "first_customer_thread", label: "First customer thread" },
	{ key: "agent_answered", label: "Agent answered" },
	{ key: "first_resolution", label: "First resolution" },
	{ key: "paying", label: "Paying" },
] as const

export type ActivationStageKey = (typeof ACTIVATION_STAGES)[number]["key"]

export interface StageWorkspace {
	hasBeenSetup: boolean
	importedArticleCount: number
	publishedArticleCount: number
	helpCenterPublic: boolean
	customDomainConnected: boolean
	emailConnected: boolean
	channelConnected: boolean
	firstCustomerThreadAt: Date | null
	firstAgentReplyAt: Date | null
	firstResolutionAt: Date | null
	hasSubscription: boolean
	isTrialing: boolean
}

const STAGE_CHECKS: Record<ActivationStageKey, (w: StageWorkspace) => boolean> =
	{
		signed_up: () => true,
		set_up: (w) => w.hasBeenSetup,
		// Hand-written docs that are already published need no import.
		help_center_imported: (w) =>
			w.importedArticleCount > 0 || w.publishedArticleCount > 0,
		help_center_live: (w) => w.helpCenterPublic && w.publishedArticleCount > 0,
		domain_connected: (w) => w.customDomainConnected,
		email_connected: (w) => w.emailConnected,
		channel_connected: (w) => w.channelConnected,
		first_customer_thread: (w) => w.firstCustomerThreadAt != null,
		agent_answered: (w) => w.firstAgentReplyAt != null,
		first_resolution: (w) => w.firstResolutionAt != null,
		paying: (w) => w.hasSubscription && !w.isTrialing,
	}

export const reachedStages = (
	w: StageWorkspace,
): Record<ActivationStageKey, boolean> =>
	Object.fromEntries(
		ACTIVATION_STAGES.map(({ key }) => [key, STAGE_CHECKS[key](w)]),
	) as Record<ActivationStageKey, boolean>

// The first stage a workspace has not met, or "paying" once it clears them all.
export const currentStage = (w: StageWorkspace): ActivationStageKey => {
	const reached = reachedStages(w)
	return ACTIVATION_STAGES.find(({ key }) => !reached[key])?.key ?? "paying"
}

export interface ChannelWorkspace {
	emailEnabled: boolean
	slackConnected: boolean
	teamsConnected: boolean
	hasWidgetThread: boolean
}

// The agent is deployed everywhere by default, so the gate is a channel that
// can actually carry a customer message. A widget only counts once installed.
export const isChannelConnected = (w: ChannelWorkspace) =>
	w.emailEnabled || w.slackConnected || w.teamsConnected || w.hasWidgetThread

export const agentUsage = (counts: {
	customerThreads: number
	agentAnswered: number
	agentResolved: number
}) => ({
	...counts,
	resolutionRate:
		counts.customerThreads > 0
			? Math.round((counts.agentResolved / counts.customerThreads) * 1000) / 10
			: null,
})

export interface ChecklistWorkspace {
	emailEnabled: boolean
	hasWidgetThread: boolean
	githubRepositoryFullName: string | null
	linearConnected: boolean
	oauthConnectionCount: number
	hasUsedApiKey: boolean
}

// Mirrors the in-app trial checklist in apps/app/src/screens/app/checklist.tsx.
export const buildSetupChecklist = (w: ChecklistWorkspace) => [
	{ key: "email", label: "Email inbox connected", done: w.emailEnabled },
	{ key: "widget", label: "Widget received a thread", done: w.hasWidgetThread },
	{
		key: "github",
		label: "GitHub repository linked",
		done: !!w.githubRepositoryFullName,
	},
	{ key: "linear", label: "Linear connected", done: w.linearConnected },
	{
		key: "mcp",
		label: "MCP connected",
		done: w.oauthConnectionCount > 0 || w.hasUsedApiKey,
	},
]
